import { useEffect, useRef } from "react";

/**
 * Calls `onReach` when a sentinel element scrolls into view.
 *
 * Pairs with `useWindowedRows`: put the returned ref on an empty element after the last row, and
 * the next page is requested as that element approaches the viewport.
 *
 * `IntersectionObserver` rather than a scroll listener. A scroll handler fires on every frame of
 * every scroll and has to measure the document to decide whether it matters, which is work on the
 * main thread during the one interaction that must stay smooth. The observer is told once what to
 * watch for and says nothing until it happens.
 *
 * `rootMargin` starts the fetch before the sentinel is actually visible, so the next page is
 * usually already merged by the time the reader arrives — the difference between a list that grows
 * and one that stalls at the bottom of every screen.
 */
export const useInfiniteScroll = (onReach: () => void, enabled: boolean, rootMargin = "400px") => {
    const sentinelRef = useRef<HTMLDivElement | null>(null);

    // Read through a ref so a caller passing an inline arrow does not tear down and rebuild the
    // observer on every render.
    const onReachRef = useRef(onReach);
    onReachRef.current = onReach;

    useEffect(() => {
        const el = sentinelRef.current;
        if (!el || !enabled) return;

        // Absent in jsdom and in older browsers. Without it the list still works — it just stops
        // growing on its own, which is a degraded page rather than a broken one.
        if (typeof IntersectionObserver === "undefined") return;

        const observer = new IntersectionObserver(
            (entries) => {
                if (entries.some((e) => e.isIntersecting)) onReachRef.current();
            },
            { rootMargin },
        );
        observer.observe(el);
        return () => observer.disconnect();
    }, [
        enabled,
        rootMargin,
    ]);

    return sentinelRef;
};
